import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import PageTitle from '../ui/PageElements/PageTitle/PageTitle'
import PageContainer from '../ui/PageElements/PageContainer/PageContainer'
import Card from '../ui/Card/Card'
import style from './style.m.less'
import request from '../../services/request'

import { Button, Checkbox, Form, Input, message, Typography } from 'antd'
import {
    loadCaptchaEnginge,
    validateCaptcha,
    LoadCanvasTemplateNoReload,
} from 'react-simple-captcha'
import InputMask from 'react-input-mask'

// import { DocumentsBlock, IFile } from '../ui'

const { TextArea } = Input
const { Paragraph } = Typography

const FeedbackForm: FC = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [form] = Form.useForm()

    const [sendClicked, setSendClicked] = useState<boolean>(false)
    // const [files, setFiles] = useState<IFile[]>([])

    useEffect(() => {
        loadCaptchaEnginge(6)
    }, [])

    const onFinish = async (values: any) => {
        setSendClicked(true)
        const ok = validateCaptcha(values.captchaCode)
        if (!ok) {
            form.setFields([{ name: 'captchaCode', errors: [t('feedback.form.captchaInvalid')] }])
            setSendClicked(false)
            return
        }
        const payload = {
            name: values.name,
            email: values.email,
            phone: values.phone ?? '',
            message: values.message,
            agree: values.agree,
        }
        /*
            files, // IFile[]
         */

        try {
            await request.post('/api/notifier/v1/send_email_feedback_form', payload)

            message.success(t('feedback.form.success'))

            form.resetFields()
            form.setFieldsValue({ phone: '' })
            // setFiles([])
            loadCaptchaEnginge(6)
            // navigate(-1)
        } catch (e) {
            message.error(t('feedback.form.error'))
        }
        setSendClicked(false)
    }

    return (
        <PageContainer className={style.container}>
            <PageTitle title={<>{t('feedback.title')}</>} />
            <Paragraph style={{ margin: '0 0 16px' }}>{t('feedback.form.text')}</Paragraph>

            <Card>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    name="feedbackForm"
                    initialValues={{ phone: '' }}
                >
                    <Form.Item
                        name="name"
                        label={`${t('feedback.form.nameLabel')}`}
                        rules={[{ required: true, message: t('feedback.form.nameRequired') }]}
                    >
                        <Input
                            className={style.input}
                            placeholder={t('feedback.form.namePlaceholder')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label={`${t('feedback.form.emailLabel')}`}
                        rules={[
                            { required: true, message: t('feedback.form.emailRequired') },
                            { type: 'email', message: t('feedback.form.emailInvalid') },
                        ]}
                        style={{ marginTop: 16 }}
                    >
                        <Input
                            className={style.input}
                            placeholder={t('feedback.form.emailPlaceholder')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="phone"
                        label={t('feedback.form.phoneLabel')}
                        rules={[
                            {
                                validator: (_: any, v?: string) => {
                                    if (!v || v.trim() === '') {
                                        return Promise.resolve()
                                    }
                                    return v.indexOf('_') >= 0
                                        ? Promise.reject(new Error(t('feedback.form.phoneInvalid')))
                                        : Promise.resolve()
                                },
                            },
                        ]}
                        style={{ marginTop: 16 }}
                        getValueFromEvent={(e) => e?.target?.value ?? ''}
                        getValueProps={(v) => ({ value: v ?? '' })}
                    >
                        <InputMask mask="+7 (999) 999-99-99" maskChar="_">
                            {(inputProps: any) => (
                                <Input
                                    {...inputProps}
                                    className={style.input}
                                    placeholder={t('feedback.form.phonePlaceholder')}
                                />
                            )}
                        </InputMask>
                    </Form.Item>

                    <Form.Item
                        name="message"
                        label={`${t('feedback.form.feedbackLabel')}`}
                        rules={[{ required: true, message: t('feedback.form.feedbackRequired') }]}
                        style={{ marginTop: 16 }}
                    >
                        <TextArea
                            rows={6}
                            className={style.input}
                            placeholder={t('feedback.form.feedbackPlaceholder')}
                            showCount
                            maxLength={3000}
                        />
                    </Form.Item>
                    {/*
                    <Form.Item>
                        <DocumentsBlock
                            value={files}
                            onChange={setFiles}
                            customStyle={{ width: 620, marginTop: 16 }}
                            description={t('feedback.form.documentsDescription')}
                        />
                    </Form.Item>
                    */}

                    <Form.Item>
                        <div className={style.agreementWrapper}>
                            <Form.Item
                                name="agree"
                                valuePropName="checked"
                                rules={[
                                    {
                                        validator: (_: any, v?: boolean) =>
                                            v
                                                ? Promise.resolve()
                                                : Promise.reject(
                                                      new Error(t('feedback.form.agreeRequired')),
                                                  ),
                                    },
                                ]}
                                noStyle
                            >
                                <Checkbox id="feedbackAgree" />
                            </Form.Item>
                            <label className={style.agreementLabel}>
                                <label htmlFor="feedbackAgree">
                                    {t('feedback.form.agreeText')}
                                </label>
                                <Link to="/documents">{t('feedback.form.agreementLink')}</Link>
                                <span style={{ marginInline: 4 }}>
                                    {t('feedback.form.agreementAnd')}
                                </span>
                                <a
                                    className={style.agreeLink}
                                    href="/documents/privacyPolicy.pdf"
                                    target="_blank"
                                >
                                    {t('feedback.form.agreementFile')}
                                </a>
                                <label className={style.star}>*</label>
                            </label>
                        </div>
                    </Form.Item>

                    <Form.Item
                        label={
                            <div style={{ display: 'inline-flex', gap: 4 }}>
                                <p style={{ color: '#FF4D4F' }}>*</p>
                                <p>{t('feedback.form.captchaLabel')}</p>
                            </div>
                        }
                        style={{ marginTop: 16 }}
                    >
                        <LoadCanvasTemplateNoReload />
                    </Form.Item>
                    <Form.Item
                        name="captchaCode"
                        rules={[{ required: true, message: t('feedback.form.captchaRequired') }]}
                    >
                        <Input
                            className={style.captchaInput}
                            placeholder={t('feedback.form.captchaPlaceholder')}
                        />
                    </Form.Item>

                    <div className={style.buttonWrapper}>
                        <Button onClick={() => navigate(-1)} className={style.primaryButtonCancel}>
                            {t('feedback.form.cancelButton')}
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            className={style.primaryButton}
                            disabled={sendClicked}
                        >
                            {t('feedback.form.sendButton')}
                        </Button>
                    </div>
                </Form>
            </Card>
        </PageContainer>
    )
}

export default FeedbackForm
